import { useState, useEffect, useLayoutEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "@/hooks/useTranslation";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { X } from "lucide-react";
import { cn } from "@/lib/utils";

const SUPPORTED_LOCALES = ["ro", "en"] as const;
const DEFAULT_LOCALE = "ro";

const COOKIE_CONSENT_KEY = "jubiel_cookie_consent";
const COOKIE_CONSENT_EXPIRY_DAYS = 365;

export type CookieConsent = {
  essential: boolean;
  analytics: boolean;
  marketing: boolean;
  timestamp: number;
};

export function getCookieConsent(): CookieConsent | null {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    const stored = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!stored) {
      return null;
    }

    const consent = JSON.parse(stored) as CookieConsent;
    // Check if consent is still valid (not expired)
    const expiryTime = consent.timestamp + COOKIE_CONSENT_EXPIRY_DAYS * 24 * 60 * 60 * 1000;
    if (Date.now() > expiryTime) {
      localStorage.removeItem(COOKIE_CONSENT_KEY);
      return null;
    }

    return consent;
  } catch {
    return null;
  }
}

export function setCookieConsent(consent: CookieConsent): void {
  if (typeof window === "undefined") {
    return;
  }

  try {
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(consent));
    // Dispatch custom event for telemetry to listen to
    window.dispatchEvent(new CustomEvent("cookieConsentUpdated", { detail: consent }));
  } catch (error) {
    console.error("Failed to save cookie consent:", error);
  }
}

export function hasCookieConsent(category: keyof Omit<CookieConsent, "timestamp">): boolean {
  const consent = getCookieConsent();
  if (!consent) {
    return false;
  }

  // Essential cookies are always allowed
  if (category === "essential") {
    return true;
  }

  return consent[category] === true;
}

/**
 * True when user has acknowledged cookies (including "Reject non-essential" where
 * essential=true) or clicked "Accept and continue" in the payment flow (JUB-226).
 * Decoupled from analytics/marketing so Stripe can load under strictly necessary
 * cookies without forcing non-essential tracking.
 */
export function hasPaymentCookieConsent(): boolean {
  const consent = getCookieConsent();
  if (!consent) return false;
  return consent.essential === true || consent.analytics === true || consent.marketing === true;
}

interface CookieConsentBannerProps {
  className?: string;
}

/**
 * JUB-63: on public event pages the page locale is decided by the event's
 * default language, which is only known after the event payload loads. The
 * banner used to mount immediately in the device language (usually EN) and
 * then flip — a visible flash for Romanian guests.
 */
function isPublicEventPath(pathname: string): boolean {
  return (
    pathname.startsWith("/@") ||
    /^\/(ro|en)\/(events|evenimente)\/[^/]+/.test(pathname)
  );
}

export function CookieConsentBanner({ className }: CookieConsentBannerProps) {
  const { t, i18n, currentLanguage } = useTranslation();
  const locale = SUPPORTED_LOCALES.includes(currentLanguage as (typeof SUPPORTED_LOCALES)[number])
    ? currentLanguage
    : DEFAULT_LOCALE;
  const cookiePolicyPath = `/${locale}/cookies-policy`;
  const [showBanner, setShowBanner] = useState(false);
  const [, setConsent] = useState<CookieConsent | null>(null);
  const bannerRef = useRef<HTMLDivElement>(null);

  // JUB-139: the banner is `position: fixed`, so it overlays document
  // content instead of pushing it up. Every page here scrolls the document
  // itself (min-h-screen sections, no nested overflow container), so a page
  // whose primary action sits at the very bottom -- Danger Zone delete, the
  // event editor Save button, the public RSVP CTA -- can end up covered
  // while the banner is undismissed, with clicks on it silently swallowed
  // by the banner instead. Two things are needed together, not just one:
  //
  // 1. `body.padding-bottom`, for actions that are the literal last thing
  //    in the document (Danger Zone, Save): there is no content below them
  //    to scroll into, so without extra room the browser's max scroll
  //    position leaves them flush against the viewport edge -- under the
  //    banner -- no matter how the user scrolls.
  // 2. `scroll-padding-bottom` on the root, for actions with more content
  //    below them (the RSVP CTA sits above a footer): a browser's native
  //    `scrollIntoView`/`scrollIntoViewIfNeeded` -- what a keyboard Tab,
  //    an anchor link, or Playwright's own actionability check all use --
  //    aligns the nearest edge flush with the viewport edge by default,
  //    which again lands it right under the banner even though there was
  //    room to scroll further. `scroll-padding-bottom` tells that native
  //    scroll math to stop short by the banner's footprint instead.
  //
  // Both are set to the banner's own measured height (not hard-coded,
  // because the banner is taller on narrow viewports where its content
  // stacks vertically) via ResizeObserver, and cleared together once the
  // banner is dismissed.
  useLayoutEffect(() => {
    if (!showBanner) {
      return;
    }

    const applyClearance = () => {
      const height = bannerRef.current?.offsetHeight ?? 0;
      if (height > 0) {
        // Banner's own bottom-4 offset (16px) plus breathing room so content
        // isn't flush against the card once scrolled clear.
        const clearance = `${height + 32}px`;
        document.body.style.paddingBottom = clearance;
        document.documentElement.style.scrollPaddingBottom = clearance;
      }
    };

    applyClearance();

    const resizeObserver = new ResizeObserver(applyClearance);
    if (bannerRef.current) {
      resizeObserver.observe(bannerRef.current);
    }
    window.addEventListener("resize", applyClearance);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener("resize", applyClearance);
      document.body.style.removeProperty("padding-bottom");
      document.documentElement.style.removeProperty("scroll-padding-bottom");
    };
  }, [showBanner]);

  useEffect(() => {
    // Check if user has already given consent
    const existingConsent = getCookieConsent();
    if (existingConsent) {
      setConsent(existingConsent);
      return;
    }

    // JUB-63: on public event pages, hold the banner until the event's
    // language has been applied (languageChanged) or a short fallback
    // elapses (event already in the right language / load failure).
    if (isPublicEventPath(window.location.pathname)) {
      let done = false;
      const reveal = () => {
        if (!done) {
          done = true;
          setShowBanner(true);
        }
      };
      i18n.on("languageChanged", reveal);
      const timer = window.setTimeout(reveal, 2000);
      return () => {
        done = true;
        i18n.off("languageChanged", reveal);
        window.clearTimeout(timer);
      };
    }

    setShowBanner(true);
  }, [i18n]);

  const handleAcceptAll = () => {
    const newConsent: CookieConsent = {
      essential: true,
      analytics: true,
      marketing: true,
      timestamp: Date.now(),
    };
    setCookieConsent(newConsent);
    setConsent(newConsent);
    setShowBanner(false);
  };

  const handleRejectAll = () => {
    const newConsent: CookieConsent = {
      essential: true, // Essential cookies are always required
      analytics: false,
      marketing: false,
      timestamp: Date.now(),
    };
    setCookieConsent(newConsent);
    setConsent(newConsent);
    setShowBanner(false);
  };

  if (!showBanner) {
    return null;
  }

  return (
    <Card
      ref={bannerRef}
      data-testid="cookie-consent-banner"
      // z-40: one layer below modal dialogs (z-50). At z-50, rendered after
      // the dialog, the banner covered a dialog's footer and swallowed clicks
      // on its Save button while consent was still pending (JUB-39).
      className={cn(
        "fixed bottom-4 left-4 right-4 z-40 w-[94%] max-w-6xl mx-auto shadow-2xl border-2",
        className
      )}
    >
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 p-3 sm:p-4">
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-foreground leading-tight">
            {t("cookies.banner.title")}
          </p>
          <p className="text-xs text-muted-foreground mt-0.5 leading-snug">
            <span>{t("cookies.banner.description")}</span>{" "}
            <span>
              {t("cookies.banner.learnMore")}{" "}
              <Link to={cookiePolicyPath} className="text-primary underline hover:no-underline inline">
                {t("cookies.banner.policyLink")}
              </Link>
            </span>
          </p>
        </div>
        <div className="flex flex-none flex-row items-center justify-end gap-2 flex-wrap">
          <Button onClick={handleAcceptAll} size="sm" className="h-8 font-normal text-xs">
            {t("cookies.banner.acceptAll")}
          </Button>
          <Button onClick={handleRejectAll} variant="outline" size="sm" className="h-8 font-normal text-xs">
            {t("cookies.banner.rejectAll")}
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 flex-shrink-0"
            onClick={handleRejectAll}
            aria-label={t("cookies.banner.close")}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </Card>
  );
}


